import { Tooltip } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { RM_COLORS } from "../rentmatch.colors";
import { BARE_TOOLTIP_TRIGGER_STYLE } from "../tooltipTrigger.styles";

/**
 * How loudly a triangle speaks.
 *
 * Amber is for something that is wrong and costs the broker something, such
 * as every visit slot being booked. Muted is for a fact worth noticing that
 * breaks nothing: a colleague without a connected calendar can still be given
 * visit moments, and everything works, so an amber triangle there claimed a
 * problem that does not exist (Jonas, 2026-10-03).
 */
export type TWarningTone = "warning" | "muted";

const TONE_COLOR: { [tone in TWarningTone]: string } = {
  warning: RM_COLORS.amber,
  muted: RM_COLORS.grayMuted,
};

/**
 * Filled or outlined.
 *
 * A solid grey triangle turned out to carry about as much weight as the amber
 * one it replaced, so the muted marker is outlined, which is what the glyph it
 * grew out of looked like in the dropdown (Jonas, 2026-10-03). The amber ones
 * stay solid; they are meant to be seen.
 */
const isOutlined = (tone: TWarningTone) => tone === "muted";

/**
 * The triangle on its own, for a place that cannot carry a tooltip: a
 * dropdown row is a button, and the shared Tooltip renders a button of its
 * own, so the native `title` has to do the explaining there.
 */
export const WarningTriangle = ({
  size = 16,
  tone = "warning",
}: {
  size?: number;
  tone?: TWarningTone;
}) => (
  <span
    style={{
      color: TONE_COLOR[tone],
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "middle",
      // The glyph's mass sits low in its 24x24 box, so centering the box
      // leaves the triangle reading about two pixels below the text beside
      // it, everywhere it is used (Jonas, 2026-10-03). A transform rather
      // than a margin, so nothing around it moves.
      transform: "translateY(-2px)",
    }}
  >
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={isOutlined(tone) ? "none" : "currentColor"}
    >
      <path
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke={isOutlined(tone) ? "currentColor" : "none"}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="9"
        x2="12"
        y2="13"
        stroke={isOutlined(tone) ? "currentColor" : "white"}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="17"
        x2="12.01"
        y2="17"
        stroke={isOutlined(tone) ? "currentColor" : "white"}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

interface IWarningTooltipProps {
  tooltipContent: React.ReactNode;
  size?: number;
  tone?: TWarningTone;
  /**
   * Space before the triangle, in pixels. It exists because most call sites
   * hang the triangle after a piece of text that has no spacing of its own.
   * A caller inside a flex row with its own gap passes 0, so the two do not
   * add up and leave the marker drifting away from what it refers to
   * (Jonas, 2026-10-03).
   */
  spacingLeft?: number;
}

/**
 * A triangle with a styled Tooltip popup, in the same palette as the
 * cross-property warning in ApplicationTableRow.
 */
export const WarningTooltip: React.FC<IWarningTooltipProps> = ({
  tooltipContent,
  size = 16,
  tone = "warning",
  spacingLeft = 5,
}) => (
  <Tooltip
    style={BARE_TOOLTIP_TRIGGER_STYLE}
    tooltipContentStyle={{
      // One popup for both tones, as it was before the muted marker existed:
      // the triangle carries the weight, the box just holds the words
      // (Jonas, 2026-10-03).
      backgroundColor: RM_COLORS.yellowBgSoft,
      border: `1px solid ${RM_COLORS.amberBorder}`,
      color: RM_COLORS.gray800,
      padding: "6px 10px",
      borderRadius: "6px",
      fontSize: "12px",
      lineHeight: "1.4",
    }}
    tooltipContent={<div style={{ maxWidth: "240px" }}>{tooltipContent}</div>}
  >
    <span style={{ cursor: "help", marginLeft: `${spacingLeft}px` }}>
      <WarningTriangle size={size} tone={tone} />
    </span>
  </Tooltip>
);
